import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, X as Multiply, Divide, ArrowRight, Check } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { ProblemType } from '../types';
interface ProblemTypeSelectorProps {
  onContinue: (types: ProblemType[]) => void;
  initialSelected?: ProblemType[];
}
export function ProblemTypeSelector({
  onContinue,
  initialSelected = []
}: ProblemTypeSelectorProps) {
  const [selected, setSelected] = useState<Set<ProblemType>>(new Set(initialSelected));
  const toggleType = (type: ProblemType) => {
    const newSelected = new Set(selected);
    if (newSelected.has(type)) {
      newSelected.delete(type);
    } else {
      newSelected.add(type);
    }
    setSelected(newSelected);
  };
  const toggleAll = () => {
    if (selected.size === 6) {
      setSelected(new Set());
    } else {
      setSelected(new Set(['addition', 'subtraction', 'multiplication', 'division', 'radical', 'exponent']));
    }
  };
  const handleContinue = () => {
    if (selected.size > 0) {
      onContinue(Array.from(selected));
    }
  };
  const types: {
    id: ProblemType;
    label: string;
    icon: React.ReactNode;
    color: string;
  }[] = [{
    id: 'addition',
    label: 'Addition',
    icon: <Plus className="w-6 h-6" data-id="element-179" />,
    color: 'bg-blue-100 text-blue-600'
  }, {
    id: 'subtraction',
    label: 'Subtraction',
    icon: <Minus className="w-6 h-6" data-id="element-180" />,
    color: 'bg-red-100 text-red-600'
  }, {
    id: 'multiplication',
    label: 'Multiplication',
    icon: <Multiply className="w-6 h-6" data-id="element-181" />,
    color: 'bg-purple-100 text-purple-600'
  }, {
    id: 'division',
    label: 'Division',
    icon: <Divide className="w-6 h-6" data-id="element-182" />,
    color: 'bg-orange-100 text-orange-600'
  }, {
    id: 'exponent',
    label: 'Exponents',
    icon: <span className="text-xl font-bold font-serif" data-id="element-183">x²</span>,
    color: 'bg-green-100 text-green-600'
  }, {
    id: 'radical',
    label: 'Radicals',
    icon: <span className="text-xl font-bold font-serif" data-id="element-184">√x</span>,
    color: 'bg-teal-100 text-teal-600'
  }];
  return <div className="w-full max-w-2xl mx-auto p-4 space-y-8" data-id="element-185">
      <Card className="p-6 md:p-8 space-y-8" data-id="element-186">
        <div className="flex justify-end" data-id="element-187">
          <button onClick={toggleAll} className="text-sm font-semibold text-indigo-600 hover:text-indigo-700" data-id="element-188">
            {selected.size === 6 ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-189">
          {types.map(type => {
          const isSelected = selected.has(type.id);
          return <motion.button key={type.id} whileTap={{
            scale: 0.98
          }} onClick={() => toggleType(type.id)} className={`relative p-4 rounded-xl border-2 transition-all h-32 flex flex-col items-center justify-center group ${isSelected ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-190">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${type.color}`} data-id="element-191">
                  {type.icon}
                </div>
                <div className="font-semibold text-slate-900 text-center" data-id="element-192">
                  {type.label}
                </div>

                {isSelected && <div className="absolute top-3 right-3 text-indigo-600" data-id="element-193">
                    <Check className="w-5 h-5" data-id="element-194" />
                  </div>}
              </motion.button>;
        })}
        </div>

        <Button size="lg" fullWidth onClick={handleContinue} disabled={selected.size === 0} className="mt-8" data-id="element-195">
          Continue <ArrowRight className="w-5 h-5 ml-2" data-id="element-196" />
        </Button>
      </Card>
    </div>;
}